import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { useAuthStore } from '@/app/store/auth'
import api from '@/lib/api/axios'

const PROVIDER_ICONS: Record<string, string> = {
    anthropic: '/images/foreground/icons/vsiconclaude.png',
    openai: '/images/foreground/icons/vsiconchatgpt.png',
    google: '/images/foreground/icons/vsicongemini.png',
}

function ProviderIcon({ provider, className }: { provider: string; className?: string }) {
    const src = PROVIDER_ICONS[provider]
    if (src) {
        return <img src={src} alt={provider} className={className || 'h-5 w-5'} />
    }
    return <div className={`${className || 'h-5 w-5'} rounded bg-muted`} />
}

function providerDotColor(provider: string): string {
    switch (provider) {
        case 'anthropic': return 'bg-orange-500'
        case 'openai': return 'bg-green-500'
        case 'google': return 'bg-blue-500'
        default: return 'bg-gray-500'
    }
}

interface EditState {
    primaryModelId: number | null
    secondaryModelId: number | null
    autoAssign: boolean
}

export function ScenarioConfigTab() {
    const { t } = useTranslation()
    const { toast } = useToast()
    const queryClient = useQueryClient()
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
    const [editingKey, setEditingKey] = useState<string | null>(null)
    const [editState, setEditState] = useState<EditState>({ primaryModelId: null, secondaryModelId: null, autoAssign: false })

    const { data: modelsData } = useQuery({
        queryKey: ['ai-models'],
        queryFn: async () => {
            const res = await api.get('/api/ai/models')
            return res.data
        },
        enabled: isAuthenticated,
    })

    const { data: preferencesData } = useQuery({
        queryKey: ['ai-model-preferences'],
        queryFn: async () => {
            const res = await api.get('/api/ai/models/preferences')
            return res.data
        },
        enabled: isAuthenticated,
    })

    const savePrefMutation = useMutation({
        mutationFn: (data: { scenarioKey: string; primaryModelId: number; secondaryModelId?: number; autoAssign?: boolean }) =>
            api.post('/api/ai/models/preferences', data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['ai-model-preferences'] })
            setEditingKey(null)
            toast({ title: t('aiModels.scenarios.preferenceSaved', 'Preference saved') })
        },
        onError: () => {
            toast({ title: t('aiModels.scenarios.preferenceSaveFailed', 'Failed to save preference'), variant: 'destructive' })
        },
    })

    const providers = modelsData?.providers || []
    const scenarios = modelsData?.scenarios || []
    const preferences: any[] = preferencesData?.preferences || []
    const prefMap = new Map(preferences.map((p: any) => [p.scenarioKey, p]))

    // Build flat model list for dropdowns
    const allModels: Array<{ id: number; modelId: string; displayName: string; providerName: string; providerDisplay: string; isDeprecated?: boolean }> = []
    for (const provider of providers) {
        for (const model of provider.models) {
            allModels.push({ id: model.id, modelId: model.modelId, displayName: model.displayName, providerName: provider.name, providerDisplay: provider.displayName, isDeprecated: model.isDeprecated })
        }
    }

    // getModelForScenario() refuses a model whose is_available is false and silently answers
    // on the default model instead, so an unavailable assignment has to be flagged here or
    // the screen keeps naming a model that is not the one answering.
    const isUnusable = (model: any): boolean => {
        if (!model) return false
        if (model.isAvailable === false) return true
        return !!allModels.find((m) => m.id === model.id)?.isDeprecated
    }

    const hasDeprecated = preferences.some((p: any) => isUnusable(p.primaryModel))

    function startEdit(scenarioKey: string) {
        const pref = prefMap.get(scenarioKey)
        setEditState({
            primaryModelId: pref?.primaryModel?.id || null,
            secondaryModelId: pref?.secondaryModel?.id || null,
            autoAssign: pref?.autoAssign || false,
        })
        setEditingKey(scenarioKey)
    }

    function saveEdit(scenarioKey: string) {
        if (!editState.primaryModelId) {
            toast({ title: t('aiModels.scenarios.selectPrimaryRequired', 'Please select a primary model'), variant: 'destructive' })
            return
        }
        savePrefMutation.mutate({
            scenarioKey,
            primaryModelId: editState.primaryModelId,
            secondaryModelId: editState.secondaryModelId || undefined,
            autoAssign: editState.autoAssign,
        })
    }

    // Sort deprecated models to bottom in dropdowns
    const sortedModels = [...allModels].sort((a, b) => {
        if (a.isDeprecated && !b.isDeprecated) return 1
        if (!a.isDeprecated && b.isDeprecated) return -1
        return 0
    })

    return (
        <div className="space-y-4">
            <div>
                <h2 className="text-lg font-semibold">{t('aiModels.scenarios.title', 'Scenario Configuration')}</h2>
                <p className="text-sm text-muted-foreground">{t('aiModels.scenarios.description', 'Set primary and fallback models for each AI scenario')}</p>
            </div>

            {hasDeprecated && (
                <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
                    <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                    <div>
                        <div className="font-medium text-sm text-destructive">{t('aiModels.scenarios.deprecatedTitle', 'Deprecated Model Assigned')}</div>
                        <div className="text-sm text-muted-foreground">{t('aiModels.scenarios.deprecatedDescription', 'One or more scenarios have a deprecated or unavailable model assigned. Review the assignments below and switch to an available alternative.')}</div>
                    </div>
                </div>
            )}

            <div className="space-y-3">
                {scenarios.map((scenario: any) => {
                    const pref = prefMap.get(scenario.scenarioKey)
                    const primaryModel = pref?.primaryModel
                    const secondaryModel = pref?.secondaryModel
                    const isPrimaryDeprecated = isUnusable(primaryModel)
                    const isClara = scenario.scenarioKey === 'clara'
                    const isEditing = editingKey === scenario.scenarioKey

                    return (
                        <div key={scenario.id} className="rounded-lg border p-4 space-y-3">
                            <div className="flex items-center gap-4">
                                {/* Provider icon */}
                                <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                                    {primaryModel?.provider ? (
                                        <ProviderIcon provider={primaryModel.provider} className="h-6 w-6" />
                                    ) : (
                                        <div className="h-6 w-6 rounded bg-muted-foreground/20" />
                                    )}
                                </div>

                                {/* Scenario info */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium text-sm">{scenario.displayName}</span>
                                        <span className="text-xs text-muted-foreground font-mono">{scenario.scenarioKey}</span>
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-0.5">{scenario.description}</div>
                                </div>

                                {/* Actions */}
                                <div className="flex-shrink-0">
                                    {isEditing ? (
                                        <div className="flex items-center gap-2">
                                            <Button size="sm" onClick={() => saveEdit(scenario.scenarioKey)} disabled={savePrefMutation.isPending}>
                                                {t('common.save', 'Save')}
                                            </Button>
                                            <Button size="sm" variant="ghost" onClick={() => setEditingKey(null)}>
                                                <X className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    ) : isPrimaryDeprecated ? (
                                        <Button size="sm" variant="destructive" onClick={() => startEdit(scenario.scenarioKey)}>
                                            {t('aiModels.scenarios.reassign', 'Reassign')}
                                        </Button>
                                    ) : (
                                        <Button size="sm" variant="outline" onClick={() => startEdit(scenario.scenarioKey)}>
                                            {t('common.edit', 'Edit')}
                                        </Button>
                                    )}
                                </div>
                            </div>

                            {/* View mode: show current assignments */}
                            {!isEditing && (
                                <div className="flex items-center gap-6 ml-14">
                                    <div>
                                        <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{t('aiModels.scenarios.primaryModel', 'Primary Model')}</div>
                                        <div className="flex items-center gap-1.5 mt-0.5">
                                            {primaryModel && (
                                                <span className={`h-2 w-2 rounded-full ${providerDotColor(primaryModel.provider)}`} />
                                            )}
                                            <span className={`text-sm ${isPrimaryDeprecated ? 'line-through text-destructive' : ''}`}>
                                                {primaryModel?.displayName || t('aiModels.scenarios.notAssigned', 'Not assigned')}
                                            </span>
                                            {isPrimaryDeprecated && (
                                                <Badge variant="destructive" className="text-[9px] py-0 px-1">{t('aiModels.scenarios.deprecatedBadge', 'DEPRECATED')}</Badge>
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{t('aiModels.scenarios.fallbackModel', 'Fallback Model')}</div>
                                        <div className="flex items-center gap-1.5 mt-0.5">
                                            {secondaryModel && (
                                                <span className={`h-2 w-2 rounded-full ${providerDotColor(secondaryModel.provider)}`} />
                                            )}
                                            <span className="text-sm">
                                                {secondaryModel?.displayName || t('aiModels.scenarios.none', 'None')}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Edit mode: dropdowns */}
                            {isEditing && (() => {
                                // Clara can ONLY use Gemini Live models (real-time bidiGenerateContent via WebSocket)
                                // Not native-audio — those are audio-only, not the Live API
                                // Other scenarios should NOT see live/realtime models
                                const isLiveModel = (modelId: string) => {
                                    const id = modelId.toLowerCase()
                                    return id.includes('live') || id.includes('realtime')
                                }
                                const isNonChatModel = (modelId: string) => {
                                    const id = modelId.toLowerCase()
                                    return id.includes('live') || id.includes('realtime') || id.includes('native-audio') || id.includes('embedding') || id.includes('robotics') || id.includes('tts') || id.includes('transcribe') || id.includes('image')
                                }
                                const availableForPrimary = isClara
                                    ? sortedModels.filter((m) => m.providerName === 'google' && isLiveModel(m.modelId))
                                    : sortedModels.filter((m) => !isNonChatModel(m.modelId))
                                const availableForFallback = isClara
                                    ? sortedModels.filter((m) => m.providerName === 'google' && isLiveModel(m.modelId))
                                    : sortedModels.filter((m) => !isNonChatModel(m.modelId))

                                return (
                                <div className="ml-14 space-y-3 p-3 rounded-lg bg-muted/30 border">
                                    {isClara && (
                                        <p className="text-xs text-muted-foreground">{t('aiModels.scenarios.claraNote', 'Clara requires Google Gemini Live / Native Audio models for real-time voice and screen monitoring. Only compatible models are shown.')}</p>
                                    )}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('aiModels.scenarios.primaryModel', 'Primary Model')}</label>
                                            <Select
                                                value={editState.primaryModelId?.toString() || ''}
                                                onValueChange={(v) => setEditState((s) => ({ ...s, primaryModelId: parseInt(v) }))}
                                            >
                                                <SelectTrigger className="h-9 text-sm">
                                                    <SelectValue placeholder={t('aiModels.scenarios.selectPrimaryPlaceholder', 'Select primary model...')} />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {availableForPrimary.map((m) => (
                                                        <SelectItem key={m.id} value={m.id.toString()}>
                                                            <div className="flex items-center gap-2">
                                                                <span className={`h-2 w-2 rounded-full ${providerDotColor(m.providerName)}`} />
                                                                <span className={m.isDeprecated ? 'line-through text-muted-foreground' : ''}>
                                                                    {m.displayName}
                                                                </span>
                                                                {m.isDeprecated && <span className="text-xs text-muted-foreground">{t('aiModels.scenarios.deprecatedSuffix', '(Deprecated)')}</span>}
                                                            </div>
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('aiModels.scenarios.fallbackModel', 'Fallback Model')}</label>
                                            <Select
                                                value={editState.secondaryModelId?.toString() || 'none'}
                                                onValueChange={(v) => setEditState((s) => ({ ...s, secondaryModelId: v === 'none' ? null : parseInt(v) }))}
                                            >
                                                <SelectTrigger className="h-9 text-sm">
                                                    <SelectValue placeholder={t('aiModels.scenarios.selectFallbackPlaceholder', 'Select fallback model...')} />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="none">{t('aiModels.scenarios.none', 'None')}</SelectItem>
                                                    {availableForFallback.map((m) => (
                                                        <SelectItem key={m.id} value={m.id.toString()}>
                                                            <div className="flex items-center gap-2">
                                                                <span className={`h-2 w-2 rounded-full ${providerDotColor(m.providerName)}`} />
                                                                <span className={m.isDeprecated ? 'line-through text-muted-foreground' : ''}>
                                                                    {m.displayName}
                                                                </span>
                                                                {m.isDeprecated && <span className="text-xs text-muted-foreground">{t('aiModels.scenarios.deprecatedSuffix', '(Deprecated)')}</span>}
                                                            </div>
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        {isClara
                                            ? t('aiModels.scenarios.claraSyncNote', 'Clara always uses Google Gemini Live. The daily sync will automatically update to the newest Gemini Live version.')
                                            : t('aiModels.scenarios.syncNote', 'The daily sync will automatically update to the newest version within the same model family (e.g. Opus stays on Opus, Sonnet stays on Sonnet).')}
                                    </p>
                                </div>
                                )
                            })()}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
